import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * Hook para manejar el estado de la URL de manera persistente
 * Permite sincronizar el estado del componente con los parámetros de la URL
 */
export const useUrlState = (key, defaultValue = null) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [value, setValue] = useState(() => {
    const urlValue = searchParams.get(key);
    return urlValue !== null ? urlValue : defaultValue;
  });

  useEffect(() => {
    const urlValue = searchParams.get(key);
    if (urlValue !== null && urlValue !== value) {
      setValue(urlValue);
    }
  }, [searchParams, key, value]);

  const updateValue = (newValue) => {
    setValue(newValue);

    const newSearchParams = new URLSearchParams(searchParams);
    if (newValue === defaultValue || newValue === null || newValue === "") {
      newSearchParams.delete(key);
    } else {
      newSearchParams.set(key, newValue);
    }

    setSearchParams(newSearchParams, { replace: true });
  };

  return [value, updateValue];
};

/**
 * Hook para manejar múltiples estados de URL
 */
export const useUrlStates = (initialStates = {}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [states, setStates] = useState(() => {
    const initial = {};
    Object.keys(initialStates).forEach((key) => {
      const urlValue = searchParams.get(key);
      initial[key] = urlValue !== null ? urlValue : initialStates[key];
    });
    return initial;
  });

  const updateState = useCallback(
    (key, value) => {
      setStates((prev) => ({ ...prev, [key]: value }));

      const newSearchParams = new URLSearchParams(searchParams);
      if (value === initialStates[key] || value === null || value === "") {
        newSearchParams.delete(key);
      } else {
        newSearchParams.set(key, value);
      }

      setSearchParams(newSearchParams, { replace: true });
    },
    [searchParams, setSearchParams, initialStates],
  );

  const updateStates = useCallback(
    (updates) => {
      setStates((prev) => ({ ...prev, ...updates }));

      const newSearchParams = new URLSearchParams(searchParams);
      Object.keys(updates).forEach((key) => {
        const value = updates[key];
        if (value === initialStates[key] || value === null || value === "") {
          newSearchParams.delete(key);
        } else {
          newSearchParams.set(key, value);
        }
      });

      setSearchParams(newSearchParams, { replace: true });
    },
    [searchParams, setSearchParams, initialStates],
  );

  return [states, updateState, updateStates];
};
